import { useState } from "react";
import { B } from "../data/pillars";

export default function CostOfInaction({ intake, avgScore }) {
  // Default values based on the provided image
  const [hourlyCost, setHourlyCost] = useState(1500);
  const [employeesAffected, setEmployeesAffected] = useState(12);
  const [hoursLost, setHoursLost] = useState(1.0);
  const [incidents, setIncidents] = useState(3);

  // Safe parsing for inputs
  const safeNum = (val) => (isNaN(parseFloat(val)) ? 0 : parseFloat(val));

  // Calculations matching the exact multiplier from the image
  // 1500 * 12 * 1.0 * 3 = 54,000 * 1.4 = 75,600
  const baseCost = safeNum(hourlyCost) * safeNum(employeesAffected) * safeNum(hoursLost) * safeNum(incidents);
  const annualDrag = baseCost * 1.4;
  const investment = annualDrag * 0.3;

  const fmt = (n) => `$${Math.round(n).toLocaleString()}`;

  const styles = {
    header: {
      background: "#0A1526",
      color: B.white,
      padding: "12px 20px",
      fontWeight: 800,
      fontSize: 14,
      letterSpacing: 1,
      textTransform: "uppercase",
      borderBottom: "1px solid #1E3A5F"
    },
    row: {
      display: "flex",
      borderBottom: "1px solid #1E3A5F",
      background: "#122240",
      flexWrap: "wrap"
    },
    labelCol: {
      flex: "1 1 250px",
      padding: "16px 20px",
      color: B.white,
      fontWeight: 600,
      fontSize: 13,
      display: "flex",
      alignItems: "center"
    },
    inputCol: {
      flex: "0 0 140px",
      padding: "16px 20px",
      borderLeft: "1px solid #1E3A5F",
      borderRight: "1px solid #1E3A5F",
      background: "#0D1B2E",
      display: "flex",
      alignItems: "center"
    },
    descCol: {
      flex: "2 1 300px",
      padding: "12px 20px",
      fontSize: 12,
      color: B.subText,
      fontStyle: "italic",
      lineHeight: 1.6,
      display: "flex",
      alignItems: "center"
    },
    input: {
      background: "transparent",
      border: "none",
      color: B.accent,
      fontWeight: 800,
      fontSize: 18,
      width: "100%",
      outline: "none",
      fontFamily: "monospace"
    }
  };

  return (
    <div style={{
      background: "#1A2E4A", borderRadius: 16, border: "1px solid #1E3A5F",
      overflow: "hidden", marginBottom: 24, boxSizing: "border-box"
    }}>
      {/* Main Title */}
      <div style={{ background: "#060E1C", padding: "20px 24px", borderBottom: "1px solid #1E3A5F" }}>
        <h2 style={{ fontSize: 22, fontWeight: 900, color: B.white, margin: "0 0 8px", letterSpacing: 0.5 }}>
          COST OF INACTION — VISUAL EDGE IT
        </h2>
        <p style={{ color: B.lightGray, fontSize: 13, fontStyle: "italic", margin: 0 }}>
          Before showing the roadmap, anchor the conversation in real numbers. What is staying at your current technology posture actually costing? Estimates are fine — the goal is a directionally correct number.
        </p>
      </div>

      {/* Inputs Section */}
      <div style={styles.header}>ENTER YOUR NUMBERS — Estimates are fine</div>
      
      <div style={styles.row}>
        <div style={styles.labelCol}>Estimated hourly cost of downtime ($)</div>
        <div style={styles.inputCol}>
          <span style={{ color: B.accent, fontWeight: 800, fontSize: 18, marginRight: 4 }}>$</span>
          <input type="number" min="0" value={hourlyCost} onChange={(e) => setHourlyCost(e.target.value)} style={styles.input} />
        </div>
        <div style={styles.descCol}>Revenue + labor cost per hour of IT outage. If unsure, use $1,500–$5,000 for SMB.</div>
      </div>

      <div style={styles.row}>
        <div style={styles.labelCol}>Employees affected by IT issues monthly</div>
        <div style={styles.inputCol}>
          <input type="number" min="0" value={employeesAffected} onChange={(e) => setEmployeesAffected(e.target.value)} style={styles.input} />
        </div>
        <div style={styles.descCol}>Users who lose productivity during incidents. Usually 30–60% of total headcount.</div>
      </div>

      <div style={styles.row}>
        <div style={styles.labelCol}>Average hours lost per incident</div>
        <div style={styles.inputCol}>
          <input type="number" step="0.1" min="0" value={hoursLost} onChange={(e) => setHoursLost(e.target.value)} style={styles.input} />
        </div>
        <div style={styles.descCol}>Time spent waiting, workarounding, or recovering. Usually 2–5 hours per event.</div>
      </div>

      <div style={styles.row}>
        <div style={styles.labelCol}>IT incidents per year (outages, failures, security events)</div>
        <div style={styles.inputCol}>
          <input type="number" min="0" value={incidents} onChange={(e) => setIncidents(e.target.value)} style={styles.input} />
        </div>
        <div style={styles.descCol}>Break-fix calls + outages + security events + performance issues. Usually 12–30 for SMB.</div>
      </div>

      {/* Annual Drag */}
      <div style={styles.header}>ANNUAL TECHNOLOGY DRAG</div>
      <div style={{ display: "flex", flexWrap: "wrap", borderBottom: "1px solid #1E3A5F", background: "#122240" }}>
        <div style={{ flex: "1 1 250px", background: B.red + "25", padding: "24px 20px", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <span style={{ color: B.white, fontWeight: 800, fontSize: 14, letterSpacing: 1 }}>ESTIMATED ANNUAL COST</span>
        </div>
        <div style={{ flex: "0 0 200px", background: B.red + "10", padding: "24px 20px", display: "flex", alignItems: "center", justifyContent: "center", borderLeft: `1px solid ${B.red}40`, borderRight: `1px solid ${B.red}40` }}>
          <span style={{ color: B.red, fontWeight: 900, fontSize: 32 }}>{fmt(annualDrag)}</span>
        </div>
        <div style={{ flex: "2 1 300px", padding: "16px 20px", fontSize: 12, color: B.lightGray, lineHeight: 1.6, display: "flex", alignItems: "center" }}>
          Reactive IT spend + lost productivity + downtime costs + risk exposure. This uses a 1.4× multiplier to account for indirect costs (management time, customer impact, compliance exposure, opportunity cost).
        </div>
      </div>

      {/* Investment Comparison */}
      <div style={styles.header}>THE INVESTMENT COMPARISON</div>
      <div style={{ display: "flex", flexWrap: "wrap", background: "#122240" }}>
        <div style={{ flex: "1 1 250px", background: B.green + "25", padding: "24px 20px", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <span style={{ color: B.white, fontWeight: 800, fontSize: 14, letterSpacing: 1, textAlign: "center" }}>TYPICAL VEIT INVESTMENT RANGE</span>
        </div>
        <div style={{ flex: "0 0 200px", background: B.green + "10", padding: "24px 20px", display: "flex", alignItems: "center", justifyContent: "center", borderLeft: `1px solid ${B.green}40`, borderRight: `1px solid ${B.green}40` }}>
          <span style={{ color: B.green, fontWeight: 900, fontSize: 32 }}>{fmt(investment)}</span>
        </div>
        <div style={{ flex: "2 1 300px", padding: "16px 20px", fontSize: 12, color: B.lightGray, lineHeight: 1.6, display: "flex", alignItems: "center" }}>
          Typical managed services engagement is 30–50% of the annual technology drag. Paid monthly. Predictable. Tax-deductible OpEx. Ask: 'Would you rather spend $X on the problem or $Y on the solution?'
        </div>
      </div>

      {/* SE Guide */}
      <div style={{ background: B.orange + "20", padding: "12px 20px", borderTop: `2px solid ${B.orange}60`, borderBottom: `1px solid ${B.orange}40` }}>
        <h3 style={{ margin: 0, color: B.orange, fontSize: 13, fontWeight: 800, letterSpacing: 1, textTransform: "uppercase", display: "flex", alignItems: "center", gap: 8 }}>
          <span>🎯</span> SE CONVERSATION GUIDE — COST OF INACTION
        </h3>
      </div>
      <div style={{ background: "#122240" }}>
        {[
          "Before entering numbers: Ask: 'If everything went down for 48 hours — email, systems, printers — what does that cost you?' Let them answer. Then use that number.",
          "On the multiplier: The 1.4× isn't padding — it's management time, customer impact, and opportunity cost. The real number is usually higher than this.",
          "On the investment comparison: Say: 'This is what staying where you are costs. The solution is a fraction of that, paid monthly.' Then be quiet.",
          "The pivot: 'So the question isn't whether you can afford managed IT. The question is whether you can afford not to have it.'"
        ].map((step, i) => (
          <div key={i} style={{ 
            padding: "16px 20px", 
            borderBottom: i < 3 ? "1px solid #1E3A5F" : "none",
            fontSize: 13, 
            color: B.lightGray, 
            lineHeight: 1.6 
          }}>
            {step}
          </div>
        ))}
      </div>
    </div>
  );
}